import { QueryData } from 'lib/api/utils';
import { useFetchMe } from 'lib/hooks/user/user.queries';

/**
 * Checks if a sub user has the specified permission URLs in their permissions array
 *
 * @param user - The user object
 * @param permissionUrls - An array of permission URLs to check against
 *
 * @returns A boolean indicating whether the user has all the specified permission URLs
 *
 * @example
 * ```
 * const user = { role: 'sub_user', permissions: '["/add-client"]' };
 * const permissionUrls = ['/add-client'];
 * const hasPermission = checkSubUserPermission(user, permissionUrls);
 * console.log(hasPermission); // true
 * ```
 */
export function checkSubUserPermissions(
  user: QueryData<typeof useFetchMe>['user'] | null,
  permissionUrls: string[]
) {
  if (!user) return false;
  if (!user.permissions) return false;

  try {
    // Check if the permissions are already an object/array
    let permissions;
    if (typeof user.permissions === 'string') {
      // If it's a string, parse it
      permissions = JSON.parse(user.permissions);
    } else {
      // If it's already an object, use it directly
      permissions = user.permissions;
    }
    // Extract the URLs from the permissions array
    const permissionUrlsFromUser = permissions.map(
      (perm: { url: string }) => perm.url
    );

    return permissionUrls.every((url) => permissionUrlsFromUser.includes(url));
  } catch (e) {
    return false;
  }
}
